import kazakhstanGeoJSON from 'utils/shared/helpers/kz.json';
import { FeatureCollection, Feature, Geometry, Point, Polygon, MultiPolygon, Position } from 'geojson';

type GeoCoordinate = Position;

function pointInGeoJSONPolygon(lng: number, lat: number, polygon: GeoCoordinate[]): boolean {
    if (polygon.length < 3) return false;

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [lng1, lat1] = polygon[i];
        const [lng2, lat2] = polygon[j];

        const intersect = ((lat1 > lat) !== (lat2 > lat)) &&
            (lng < (lng2 - lng1) * (lat - lat1) / (lat2 - lat1) + lng1);

        if (intersect) inside = !inside;
    }
    return inside;
}

function checkPointInPolygonCoordinates(lat: number, lng: number, coordinates: Position[][]): boolean {
    if (coordinates.length === 0) return false;

    const outerRing = coordinates[0];
    if (pointInGeoJSONPolygon(lng, lat, outerRing)) {
        if (coordinates.length === 1) return true;

        let insideHole = false;
        for (let i = 1; i < coordinates.length; i++) {
            const hole = coordinates[i];
            if (pointInGeoJSONPolygon(lng, lat, hole)) {
                insideHole = true;
                break;
            }
        }
        if (!insideHole) return true;
    }
    return false;
}

function checkPointInMultiPolygonCoordinates(lat: number, lng: number, coordinates: Position[][][]): boolean {
    for (const polygonRings of coordinates) {
        if (checkPointInPolygonCoordinates(lat, lng, polygonRings)) {
            return true;
        }
    }
    return false;
}

function calculateBoundsFromGeoJSON(): [[number, number], [number, number]] {
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    const featureCollection = kazakhstanGeoJSON as FeatureCollection;

    const processCoordinates = (coords: Position | Position[] | Position[][] | Position[][][]) => {
        if (typeof coords[0] === 'number') {
            const [lng, lat] = coords as Position;
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
        } else {
            (coords as Position[] | Position[][] | Position[][][]).forEach(processCoordinates);
        }
    };

    if (featureCollection.type === 'FeatureCollection' && featureCollection.features) {
        for (const feature of featureCollection.features) {
            const geom = feature.geometry as Geometry;
            if (geom && (geom.type === 'Polygon' || geom.type === 'MultiPolygon')) {
                processCoordinates((geom as Polygon | MultiPolygon).coordinates);
            }
        }
    }

    return [
        [minLat, minLng],
        [maxLat, maxLng],
    ];
}

const KAZAKHSTAN_BOUNDS = calculateBoundsFromGeoJSON();

export function isPointInKazakhstan(latitude: number, longitude: number): boolean {
    if (isNaN(latitude) || isNaN(longitude) || (latitude === 0 && longitude === 0)) {
        return false;
    }

    const [minLat, minLng] = KAZAKHSTAN_BOUNDS[0];
    const [maxLat, maxLng] = KAZAKHSTAN_BOUNDS[1];

    if (latitude < minLat || latitude > maxLat || longitude < minLng || longitude > maxLng) {
        return false;
    }

    try {
        const featureCollection = kazakhstanGeoJSON as FeatureCollection;
        if (featureCollection.type === 'FeatureCollection' && featureCollection.features) {
            for (const feature of featureCollection.features) {
                const geom = feature.geometry as Geometry;
                if (geom) {
                    if (geom.type === 'MultiPolygon') {
                        const multiPolygon = geom as MultiPolygon;
                        if (checkPointInMultiPolygonCoordinates(latitude, longitude, multiPolygon.coordinates)) {
                            return true;
                        }
                    } else if (geom.type === 'Polygon') {
                        const polygon = geom as Polygon;
                        if (checkPointInPolygonCoordinates(latitude, longitude, polygon.coordinates)) {
                            return true;
                        }
                    }
                }
            }
        }
    } catch (error) {
        console.error('Ошибка при проверке точки:', error);
        return false;
    }

    return false;
}

